
/**
 * S.A.L. Logistics Metrics
 * Lightweight telemetry to track Fallback vs Real Data usage.
 * 
 * Strategy:
 * - Simple counters stored in localStorage.
 * - Writes are throttled/debounced (implied by usage pattern or explicit check).
 * - Exception safe (try/catch).
 */

const METRICS_KEY = 'LOGISTICS_ESTIMATE_USAGE_V1';

interface EstimateUsageStats {
    totalCalls: number;
    totalFallback: number;
    totalLearned: number;
    bySource: Record<string, { fallback: number, learned: number }>;
    lastUpdated: number;
}

const getEmptyStats = (): EstimateUsageStats => ({
    totalCalls: 0,
    totalFallback: 0,
    totalLearned: 0,
    bySource: {},
    lastUpdated: Date.now()
});

let inMemoryStats: EstimateUsageStats | null = null;
let saveTimeout: any = null;

const loadStats = (): EstimateUsageStats => {
    if (inMemoryStats) return inMemoryStats;
    try {
        const raw = localStorage.getItem(METRICS_KEY);
        if (raw) {
            inMemoryStats = JSON.parse(raw);
        }
    } catch (e) {
        // Silent fail
    }
    if (!inMemoryStats) inMemoryStats = getEmptyStats();
    return inMemoryStats!;
};

const persistStats = () => {
    if (!inMemoryStats) return;
    try {
        inMemoryStats.lastUpdated = Date.now();
        localStorage.setItem(METRICS_KEY, JSON.stringify(inMemoryStats));
    } catch (e) {
        // Quota exceeded or restrictive environment
    }
};

/**
 * Records a usage of the estimation engine.
 * @param source Identifier of the caller (e.g., 'timing', 'clustering', 'ui')
 * @param usedFallback True if the system fell back to COLD_START constants.
 * @param _speedKmh Optional: the speed value used (for future logging)
 */
export const recordEstimateUsage = (params: { source: string, usedFallback: boolean, speedKmh?: number }) => {
    const stats = loadStats();

    stats.totalCalls++;
    if (params.usedFallback) {
        stats.totalFallback++;
    } else {
        stats.totalLearned++;
    }

    if (!stats.bySource[params.source]) {
        stats.bySource[params.source] = { fallback: 0, learned: 0 };
    }

    if (params.usedFallback) {
        stats.bySource[params.source].fallback++;
    } else {
        stats.bySource[params.source].learned++;
    }

    // Debounce save (2 seconds)
    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(persistStats, 2000);
};

export const getEstimateUsageStats = (): EstimateUsageStats => {
    return loadStats();
};
